import React from 'react';
import { SpinnerIcon, WarningIcon } from '@phosphor-icons/react';

type LoadingVariant = 'default' | 'error' | 'warning';

interface LoadingProps {
  text?: string;
  variant?: LoadingVariant;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Renders the spinner centred in the full viewport height — use in place of FullScreenLoading */
  fullscreen?: boolean;
}

const Loading: React.FC<LoadingProps> = ({
  text = 'Loading...',
  variant = 'default',
  size = 'md',
  className = '',
  fullscreen = false
}) => {
  const getVariantStyles = (variant: LoadingVariant) => {
    switch (variant) {
      case 'error':
        return {
          iconColor: 'text-misc-error',
          textColor: 'text-misc-error'
        };
      case 'warning':
        return {
          iconColor: 'text-yellow-500',
          textColor: 'text-yellow-500'
        };
      default:
        return {
          iconColor: 'text-content-subtlest',
          textColor: 'text-content-subtlest'
        };
    }
  };

  const getSizeStyles = (size: 'sm' | 'md' | 'lg') => {
    switch (size) {
      case 'sm':
        return {
          iconSize: 16,
          textSize: 'text-sm',
          gap: 'gap-2'
        };
      case 'lg':
        return {
          iconSize: 24,
          textSize: 'text-lg',
          gap: 'gap-4'
        };
      default:
        return {
          iconSize: 24,
          textSize: 'text-base',
          gap: 'gap-2'
        };
    }
  };

  const getIcon = (variant: LoadingVariant, iconSize: number, iconColor: string) => {
    const baseClasses = `${iconColor} flex-shrink-0`;

    switch (variant) {
      case 'error':
        return <WarningIcon size={iconSize} className={baseClasses} />;
      case 'warning':
        return <WarningIcon size={iconSize} className={baseClasses} />;
      default:
        return <SpinnerIcon size={iconSize} className={`${baseClasses} animate-spin`} />;
    }
  };

  const variantStyles = getVariantStyles(variant);
  const sizeStyles = getSizeStyles(size);

  const inner = (
    <div
      className={`
      inline-flex items-center justify-center
      ${sizeStyles.gap}
      ${fullscreen ? '' : className}
    `}
    >
      {getIcon(variant, sizeStyles.iconSize, variantStyles.iconColor)}
      <span className={`${variantStyles.textColor} ${sizeStyles.textSize} font-medium`}>{text}</span>
    </div>
  );

  if (fullscreen) {
    return (
      <div className={`flex flex-col h-screen w-full items-center justify-center text-content-subtle ${className}`}>
        {inner}
      </div>
    );
  }

  return inner;
};

export default Loading;

// Demo component to showcase different variants
// const LoadingDemo: React.FC = () => {
//   return (
//     <div className="p-8 space-y-6 bg-gray-100 min-h-screen">
//       <h1 className="text-2xl font-bold text-gray-800 mb-8">Loading Component Variants</h1>

//       <div className="space-y-4">
//         <h2 className="text-lg font-semibold text-gray-700">Default Variant</h2>
//         <div className="flex flex-wrap gap-4">
//           <Loading variant="default" size="sm" text="Loading..." />
//           <Loading variant="default" size="md" text="Processing data..." />
//           <Loading variant="default" size="lg" text="Please wait while we load your content" />
//         </div>
//       </div>

//       <div className="space-y-4">
//         <h2 className="text-lg font-semibold text-gray-700">Warning Variant</h2>
//         <div className="flex flex-wrap gap-4">
//           <Loading variant="warning" size="sm" text="Connection slow" />
//           <Loading variant="warning" size="md" text="This might take a while..." />
//           <Loading variant="warning" size="lg" text="Warning: Large file processing" />
//         </div>
//       </div>

//       <div className="space-y-4">
//         <h2 className="text-lg font-semibold text-gray-700">Error Variant</h2>
//         <div className="flex flex-wrap gap-4">
//           <Loading variant="error" size="sm" text="Failed to load" />
//           <Loading variant="error" size="md" text="Connection timeout" />
//           <Loading variant="error" size="lg" text="Error: Unable to process request" />
//         </div>
//       </div>

//       <div className="space-y-4">
//         <h2 className="text-lg font-semibold text-gray-700">Custom Styling</h2>
//         <div className="flex flex-wrap gap-4">
//           <Loading
//             variant="default"
//             text="Custom styled loading"
//             className="rounded-full shadow-lg"
//           />
//           <Loading
//             variant="warning"
//             text="With custom classes"
//             className="border-2 border-dashed"
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default LoadingDemo;
